/**
 * Instagram Slider section.
 *
 * Up to 3 nested flickity sliders per section instance:
 *   1. Main grid slider (.ig-slider) — images + handles on homepage.
 *   2. Modal detail slider (.ig-modal-slider) — outer, navigates between
 *      posts. Lazy-init on first modal open (flickity needs a visible
 *      container to measure cell widths).
 *   3. Inner image slider (.ig-modal-slide__image-slider) — per-post,
 *      only present when post's `images` list has >1 item. Lazy-init
 *      when the parent modal slide is selected (per-index cache so we
 *      don't re-init on revisit).
 *
 * Drag isolation is SPATIAL and GATED on multi-image. For posts with
 * a nested image slider (>1 image), pointer/touch events originating
 * inside .ig-modal-slide__image are stopPropagation'd before bubbling
 * reaches the outer Flickity's viewport — so drags over the image
 * only scroll the inner slider. For single-image posts, no isolation
 * is installed: drags anywhere (including over the image) still reach
 * the outer slider and navigate posts normally. Outer dragThreshold
 * stays at 30 as a light buffer for edge-of-image touches.
 *
 * Flash-free modal open. Root cause of every prior attempt's residual
 * delay: theme.css.liquid has `.modal-open .modal .modal__inner {
 * animation: modal-open 0.5s forwards }` (theme.js's openClass runs
 * a 500ms fade-in animation on modal__inner). Hiding modal__inner via
 * CSS visibility-after-open was racing the theme animation; killing
 * the animation made modal__inner appear instantly with no image
 * inside.
 *
 * The definitive fix is to never let theme.Modals.open() fire until
 * the cell's first image is fully loaded AND decoded. While we wait
 * silently, the clicked tile gets `.is-loading` (cursor:wait) so the
 * user knows the click registered. Once image is decoded, modal opens
 * with image already in cache + decoded — paints on first frame.
 *
 *   1. PAGE-LOAD PRELOAD (_preloadModalCoverImages): immediately at
 *      section init, `new Image()` background fetch all modal cover
 *      images. By tile-click time, cover is typically already cached
 *      and the wait below is near-zero.
 *   2. ON-CLICK DIRECT-SRC (_unveilImagesForCell): on click, set
 *      img.src directly with the same URL (computed identically).
 *      Browser cache hit → fast load.
 *   3. WAIT-BEFORE-OPEN (_waitForImageReady): wait for both <img>.load
 *      event AND img.decode() Promise to resolve before calling
 *      modal.open(). 2500ms safety timeout in case load/decode hang.
 *   4. CSS BELT-AND-BRACES: visibility:hidden + opacity:0 on
 *      modal__inner with !important, flipped to visible by
 *      `.is-modal-ready` class added after Flickity init.
 *
 * Piggy-backs on theme.Slideshow, theme.Modals, and theme.sections from
 * theme.min.js. Loaded with `defer` from instagram-slider.liquid, so it
 * runs after theme.min.js parses but before DOMContentLoaded handlers
 * fire — at which point theme.sections.register() can synchronously
 * find and instantiate sections already in the DOM.
 */
(function () {
  if (typeof theme === 'undefined' || !theme.sections || !theme.Slideshow || !theme.Modals) {
    return;
  }
  if (theme.sections.constructors && theme.sections.constructors['instagram-slider']) {
    return;
  }

  function InstagramSlider(container) {
    this.container = container;
    this.sectionId = container.getAttribute('data-section-id');
    this.modalId = container.getAttribute('data-modal-id');
    this.mainEl = container.querySelector('.ig-slider');
    this.modalEl = container.querySelector('.ig-modal-slider');
    this.modalSlider = null;
    this.imageSliders = {};

    if (!this.mainEl) {
      return;
    }

    var count = parseInt(this.mainEl.getAttribute('data-count'), 10) || 0;
    var cellsDesktop = parseInt(this.mainEl.getAttribute('data-cells-desktop'), 10) || 4;
    var wantsDots = this.mainEl.getAttribute('data-dots') === 'true';
    var wantsArrows = this.mainEl.getAttribute('data-arrows') === 'true';

    // Only slide if there's overflow to scroll through.
    var shouldSlide = count > cellsDesktop;

    this.flickity = new theme.Slideshow(this.mainEl, {
      pageDots: wantsDots && shouldSlide,
      prevNextButtons: wantsArrows && shouldSlide,
      wrapAround: shouldSlide,
      cellAlign: 'left',
      contain: !shouldSlide,
      groupCells: true,
      adaptiveHeight: false,
      draggable: shouldSlide
    });

    if (document.getElementById(this.modalId)) {
      this.modal = new theme.Modals(this.modalId, 'ig-modal-' + this.sectionId);
    }

    this._bindClicks();
    this._setupTaggedProducts();
    this._preloadModalCoverImages();
  }

  InstagramSlider.prototype._bindClicks = function () {
    var self = this;
    var triggers = this.mainEl.querySelectorAll('.ig-slide__image-btn');
    triggers.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var idx = parseInt(btn.getAttribute('data-ig-index'), 10) || 0;
        // Visual feedback during the silent wait for image to load.
        // Without this, user clicks and sees no change until modal
        // opens — feels like the click was lost.
        btn.classList.add('is-loading');
        self._openModalAt(idx);
      });
    });
  };

  /**
   * Two-phase open:
   *
   *   Phase 1 — wait silently. Modal NEVER opens until cell's first
   *             image is fully loaded AND decoded. While we wait, the
   *             clicked tile gets `.is-loading` (cursor:wait). User
   *             sees no modal, no animation, no progressive content
   *             — just brief wait, then everything appears together.
   *
   *   Phase 2 — open. After image is ready, theme.Modals.open() runs;
   *             we kill the native 0.5s modal-open animation via CSS
   *             so modal appears instantly (already-loaded image
   *             paints on first frame). Then init Flickity, add
   *             `.is-modal-ready` to flip CSS visibility gate as
   *             belt-and-braces.
   *
   * Safety timeout 2500ms in phase 1 means slow networks still get the
   * modal eventually (no infinite click-to-nothing trap).
   */
  InstagramSlider.prototype._openModalAt = function (index) {
    if (!this.modal || !this.modalEl) {
      return;
    }
    var self = this;
    // Set img.src directly — starts (or reuses) the network fetch.
    self._unveilImagesForCell(index);
    // Wait for image fully ready, THEN actually open the modal.
    self._waitForImageReady(index, function () {
      self._openModalNow(index);
    });
  };

  InstagramSlider.prototype._waitForImageReady = function (index, callback) {
    var cell = this.modalEl.querySelector('.ig-modal-slide[data-index="' + index + '"]');
    var firstImg = cell ? cell.querySelector('img') : null;
    var done = false;
    var finish = function () {
      if (done) {
        return;
      }
      done = true;
      callback();
    };
    if (!firstImg) {
      finish();
      return;
    }
    var afterLoad = function () {
      if (typeof firstImg.decode === 'function') {
        firstImg.decode().then(finish).catch(finish);
      } else {
        finish();
      }
    };
    if (firstImg.complete && firstImg.naturalWidth > 0) {
      afterLoad();
    } else {
      firstImg.addEventListener('load', afterLoad, { once: true });
      firstImg.addEventListener('error', finish, { once: true });
    }
    window.setTimeout(finish, 2500);
  };

  InstagramSlider.prototype._openModalNow = function (index) {
    var self = this;
    self.modal.open();
    window.setTimeout(function () {
      if (!self.modalSlider) {
        self.modalSlider = new theme.Slideshow(self.modalEl, {
          pageDots: false,
          prevNextButtons: true,
          wrapAround: true,
          adaptiveHeight: true,
          draggable: true,
          cellAlign: 'center',
          groupCells: false,
          initialIndex: index,
          dragThreshold: 30
        });
        self._installSpatialDragIsolation();
        if (typeof self.modalSlider.on === 'function') {
          self.modalSlider.on('select', function () {
            var newIdx = (typeof self.modalSlider.selectedIndex === 'number')
              ? self.modalSlider.selectedIndex
              : null;
            if (newIdx !== null) {
              self._initImageSliderForIndex(newIdx);
              self._unveilImagesForCell(newIdx);
            }
          });
        }
      } else {
        self.modalSlider.goToSlide(index);
      }
      self._initImageSliderForIndex(index);
      if (self.modalSlider && typeof self.modalSlider.resize === 'function') {
        self.modalSlider.resize();
      }
      // Belt-and-braces visibility flip + clear tile loading feedback.
      var modalRoot = document.getElementById(self.modalId);
      if (modalRoot) {
        modalRoot.classList.add('is-modal-ready');
      }
      self.mainEl.querySelectorAll('.ig-slide__image-btn.is-loading').forEach(function (btn) {
        btn.classList.remove('is-loading');
      });
    }, 50);
  };

  /**
   * Preload all modal cover images immediately at section init (typically
   * DOMContentLoaded). Uses `new Image()` — populates browser HTTP cache
   * without DOM pollution. By the time the user clicks a tile, the cover
   * image is typically already cached → modal opens with content
   * instantly via _unveilImagesForCell (same URL = cache hit).
   *
   * NOT idle-scheduled: idle callbacks can wait 1-3s for the browser to
   * find quiet time, which on busy Shopify themes means preload fires
   * AFTER the user has already clicked → cache miss → visible delay
   * (was the original bug). Triggering immediately gives ~500ms-2s of
   * preload-before-click on typical user behavior (scroll to section
   * then click), enough to land the image in cache first.
   *
   * Using `new Image()` instead of <link rel="preload">: same cache
   * effect, but no "preloaded but not used within X seconds" console
   * warnings when the user opens fewer tiles than were preloaded.
   */
  InstagramSlider.prototype._preloadModalCoverImages = function () {
    if (!this.modalEl) {
      return;
    }
    var self = this;
    var slides = this.modalEl.querySelectorAll('.ig-modal-slide');
    slides.forEach(function (slide) {
      var firstImg = slide.querySelector('img');
      if (!firstImg) {
        return;
      }
      var src = self._computeModalImageSrc(firstImg);
      if (!src) {
        return;
      }
      var preloader = new Image();
      preloader.src = src;
    });
  };

  /**
   * Compute a concrete image URL from a lazysizes-style <img>:
   * resolves the `{width}` placeholder in data-src by picking the
   * closest size from data-widths to the expected modal display
   * width (× devicePixelRatio for retina sharpness). Used by both
   * page-load preload and on-click direct-src assignment so both
   * hit the same cached URL.
   */
  InstagramSlider.prototype._computeModalImageSrc = function (img) {
    var src = img.dataset.src || img.getAttribute('data-src') || img.src;
    if (!src) {
      return null;
    }
    if (src.indexOf('{width}') === -1) {
      return src;
    }
    var widthsAttr = img.dataset.widths || img.getAttribute('data-widths');
    var pickWidth = 720;
    if (widthsAttr) {
      try {
        var arr = JSON.parse(widthsAttr);
        var vw = window.innerWidth || document.documentElement.clientWidth || 1080;
        var dpr = window.devicePixelRatio || 1;
        // Modal image area: ~62% of modal__inner on desktop (max 1080),
        // ~full viewport on mobile. Cap at 720 to avoid mega downloads.
        var target = (vw < 769 ? Math.min(vw, 600) : Math.min(vw * 0.62, 720)) * dpr;
        pickWidth = arr.reduce(function (prev, curr) {
          return Math.abs(curr - target) < Math.abs(prev - target) ? curr : prev;
        });
      } catch (e) { /* fall back to default 720 */ }
    }
    return src.replace('{width}', pickWidth);
  };

  /**
   * Trigger immediate image load for the cell at `index` by setting
   * `img.src` directly (bypasses lazysizes — no dependency on
   * lazySizes.loader.unveil API, which may or may not be exposed by
   * the theme's bundled lazysizes build). Pairs with the page-load
   * preload above: src URL is computed identically, so the browser
   * serves from cache when available.
   */
  InstagramSlider.prototype._unveilImagesForCell = function (index) {
    if (!this.modalEl) {
      return;
    }
    var cell = this.modalEl.querySelector('.ig-modal-slide[data-index="' + index + '"]');
    if (!cell) {
      return;
    }
    var self = this;
    var lazyImgs = cell.querySelectorAll('img.lazyload');
    lazyImgs.forEach(function (img) {
      if (img.src) {
        return;
      }
      var src = self._computeModalImageSrc(img);
      if (!src) {
        return;
      }
      img.src = src;
      img.classList.remove('lazyload');
      img.classList.add('lazyloaded');
    });
  };

  /**
   * Lazy-init Flickity for the nested image slider of a single post.
   * Cached per index so revisiting a post doesn't re-init (keeps the
   * user's last-viewed image position). No-op for single-image posts.
   */
  InstagramSlider.prototype._initImageSliderForIndex = function (index) {
    if (this.imageSliders[index]) {
      return;
    }
    var inner = this.modalEl.querySelector(
      '.ig-modal-slide__image-slider[data-post-index="' + index + '"]'
    );
    if (!inner) {
      return;
    }
    var imageCount = parseInt(inner.getAttribute('data-image-count'), 10) || 0;
    if (imageCount <= 1) {
      return;
    }
    var innerInst = new theme.Slideshow(inner, {
      pageDots: true,
      prevNextButtons: false,
      wrapAround: false,
      contain: true,
      cellAlign: 'center',
      groupCells: false,
      adaptiveHeight: false,
      draggable: true
    });
    this.imageSliders[index] = innerInst;
  };

  /**
   * Spatial drag isolation, gated on multi-image. Only posts whose
   * .ig-modal-slide__image contains a nested .ig-modal-slide__image-slider
   * (images list size > 1) get the stopPropagation guard. Single-image
   * posts skip — drags over their image still reach outer Flickity and
   * navigate posts normally.
   */
  InstagramSlider.prototype._installSpatialDragIsolation = function () {
    if (!this.modalEl) {
      return;
    }
    var images = this.modalEl.querySelectorAll('.ig-modal-slide__image');
    var stop = function (e) { e.stopPropagation(); };
    images.forEach(function (img) {
      if (!img.querySelector('.ig-modal-slide__image-slider')) {
        return;
      }
      img.addEventListener('pointerdown', stop, { passive: true });
      img.addEventListener('touchstart', stop, { passive: true });
      img.addEventListener('mousedown', stop);
    });
  };

  /**
   * Tagged products in modal: repurpose native quick-shop button into
   * a "view product in new tab" trigger, scoped to IG modal only.
   *
   * - If native button rendered by product-grid-item snippet, strip its
   *   `js-modal-open-quick-modal-{id}` class so theme.min.js's global
   *   handler ignores it. Tag with `ig-qs-btn` for our delegation.
   * - If not rendered (settings.quick_shop_enable === false globally),
   *   inject visually identical button so the IG modal feature works
   *   regardless of global setting.
   * - Click goes through one delegated capture-phase listener per
   *   product container, so native handlers (if any survived) never
   *   fire on these buttons.
   */
  InstagramSlider.prototype._setupTaggedProducts = function () {
    var containers = this.container.querySelectorAll('[data-ig-products]');
    if (!containers.length) {
      return;
    }
    var self = this;
    containers.forEach(function (container) {
      var label = container.getAttribute('data-qs-label') || 'View product';
      var items = container.querySelectorAll('.grid-product');
      items.forEach(function (item) {
        self._repurposeQuickShopButton(item, label);
      });
      // Capture-phase delegation: fires before theme.min.js bubble-phase
      // handlers, and stopImmediatePropagation halts any remaining listeners.
      container.addEventListener('click', function (e) {
        var btn = e.target.closest('.ig-qs-btn');
        if (!btn) {
          return;
        }
        e.preventDefault();
        e.stopImmediatePropagation();
        var card = btn.closest('.grid-product');
        var handle = card && card.getAttribute('data-product-handle');
        if (handle) {
          window.open('/products/' + handle, '_blank', 'noopener,noreferrer');
        }
      }, true);
    });
  };

  InstagramSlider.prototype._repurposeQuickShopButton = function (item, label) {
    var existing = item.querySelector('.quick-product__btn');
    if (existing) {
      // Strip native handler hook class (any js-modal-open-quick-modal-*).
      var classes = (existing.className || '').split(/\s+/).filter(function (c) {
        return c && c.indexOf('js-modal-open-quick-modal-') !== 0;
      });
      classes.push('ig-qs-btn');
      existing.className = classes.join(' ');
      return;
    }
    // Inject manually when global quick_shop_enable is false.
    var mask = item.querySelector('.grid-product__image-mask');
    if (!mask) {
      return;
    }
    var btn = document.createElement('div');
    btn.className = 'quick-product__btn quick-product__btn--not-ready ig-qs-btn small--hide';
    var labelEl = document.createElement('span');
    labelEl.className = 'quick-product__label';
    labelEl.textContent = label;
    btn.appendChild(labelEl);
    mask.insertBefore(btn, mask.firstChild);
  };

  InstagramSlider.prototype.onUnload = function () {
    if (this.flickity && typeof this.flickity.destroy === 'function') {
      this.flickity.destroy();
    }
    if (this.modalSlider && typeof this.modalSlider.destroy === 'function') {
      this.modalSlider.destroy();
    }
    var sliders = this.imageSliders || {};
    Object.keys(sliders).forEach(function (key) {
      var inst = sliders[key];
      if (inst && typeof inst.destroy === 'function') {
        inst.destroy();
      }
    });
    this.imageSliders = {};
  };

  InstagramSlider.prototype.onBlockSelect = function () {
    // No-op: content is metaobject-driven, not blocks.
  };

  theme.sections.register('instagram-slider', InstagramSlider);
})();
